import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

interface Settings {
  enabled: boolean;
  weekday: number;
  hour: number;
  recipients: string[];
  timezone: string;
}

const DEFAULTS: Settings = {
  enabled: false,
  weekday: 5,
  hour: 8,
  recipients: [],
  timezone: 'Europe/Vilnius',
};

export function LowStockReportSettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [recipients, setRecipients] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<Settings>('/settings/low-stock-report')
      .then((value) => {
        setSettings(value);
        setRecipients(value.recipients.join('\n'));
      })
      .catch(() => setSettings(DEFAULTS));
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!settings) return;
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await api<Settings>('/settings/low-stock-report', {
        method: 'PUT',
        body: {
          enabled: settings.enabled,
          weekday: settings.weekday,
          hour: settings.hour,
          recipients: recipients
            .split(/[\n,;]+/)
            .map((email) => email.trim())
            .filter(Boolean),
        },
      });
      setSettings(updated);
      setRecipients(updated.recipients.join('\n'));
      setSaved(true);
    } catch {
      setError(t('admin.low_stock.error'));
    } finally {
      setBusy(false);
    }
  }

  if (!settings) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="max-w-2xl space-y-4">
      <header>
        <h2 className="text-xl font-semibold">{t('admin.low_stock.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('admin.low_stock.subtitle')}</p>
      </header>

      <form
        onSubmit={(event) => void submit(event)}
        className="space-y-4 rounded-md border border-border p-4"
      >
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(event) => setSettings({ ...settings, enabled: event.target.checked })}
          />
          <span className="font-medium">{t('admin.low_stock.enabled')}</span>
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="font-medium">{t('admin.low_stock.weekday')}</span>
            <select
              value={settings.weekday}
              onChange={(event) =>
                setSettings({ ...settings, weekday: Number(event.target.value) })
              }
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <option key={day} value={day}>
                  {t(`admin.low_stock.weekday.${day}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1 text-sm">
            <span className="font-medium">{t('admin.low_stock.hour')}</span>
            <input
              type="number"
              min={0}
              max={23}
              value={settings.hour}
              onChange={(event) => setSettings({ ...settings, hour: Number(event.target.value) })}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
        </div>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">{t('admin.low_stock.recipients')}</span>
          <textarea
            value={recipients}
            onChange={(event) => setRecipients(event.target.value)}
            rows={5}
            placeholder={t('admin.low_stock.recipients_placeholder')}
            className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono text-xs"
          />
          <span className="block text-xs text-muted-foreground">
            {t('admin.low_stock.timezone').replace('{{timezone}}', settings.timezone)}
          </span>
        </label>

        {error ? <div className="text-sm text-red-600">{error}</div> : null}
        {saved ? <div className="text-sm text-green-700">{t('admin.low_stock.saved')}</div> : null}
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('common.save')}
        </button>
      </form>
    </section>
  );
}
